import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, addMonths, addYears, eachDayOfInterval, eachMonthOfInterval } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, ChevronLeft, ChevronRight, RotateCcw, AlertCircle } from 'lucide-react';

export default function Charts() {
  const [chartPeriod, setChartPeriod] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [chartData, setChartData] = useState([]);
  const [topIncomeCategories, setTopIncomeCategories] = useState([]);
  const [topExpenseCategories, setTopExpenseCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const processChartData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let startDate;
      let endDate;
      let intervals;

      // Determine date range based on period
      if (chartPeriod === 'week') {
        startDate = startOfWeek(currentDate);
        endDate = endOfWeek(currentDate);
        intervals = eachDayOfInterval({ start: startDate, end: endDate });
      } else if (chartPeriod === 'month') {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
        intervals = eachDayOfInterval({ start: startDate, end: endDate }).filter((d, i) => i % 2 === 0);
      } else {
        startDate = subMonths(startOfMonth(currentDate), 11);
        endDate = endOfMonth(currentDate);
        intervals = eachMonthOfInterval({ start: startDate, end: endDate });
      }

      // Fetch all income and expenses for the period
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const [incomeRes, expensesRes] = await Promise.all([
        axios.get(`${API}/income`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/expenses`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const allIncome = Array.isArray(incomeRes.data) ? incomeRes.data : [];
      const allExpenses = Array.isArray(expensesRes.data) ? expensesRes.data : [];

      // Filter data by date range using string comparison
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      const filteredIncome = allIncome.filter(i => {
        if (!i?.date) return false;
        const dateStr = i.date.substring(0, 10);
        return dateStr >= startStr && dateStr <= endStr;
      });
      const filteredExpenses = allExpenses.filter(e => {
        if (!e?.date) return false;
        const dateStr = e.date.substring(0, 10);
        return dateStr >= startStr && dateStr <= endStr;
      });

      // Process time series data
      const timeSeriesData = intervals.map(date => {
        const dateStr = format(date, chartPeriod === 'year' ? 'MMM yyyy' : 'MMM dd');
        const currentDateStr = format(date, 'yyyy-MM-dd');

        if (chartPeriod === 'year') {
          const targetMonth = date.getMonth();
          const targetYear = date.getFullYear();
          
          const monthIncome = filteredIncome.filter(i => {
            const parts = i.date.substring(0, 10).split('-');
            return parseInt(parts[1]) - 1 === targetMonth && parseInt(parts[0]) === targetYear;
          });
          const monthExpenses = filteredExpenses.filter(e => {
            const parts = e.date.substring(0, 10).split('-');
            return parseInt(parts[1]) - 1 === targetMonth && parseInt(parts[0]) === targetYear;
          });

          return {
            name: dateStr,
            income: monthIncome.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
            expenses: monthExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)
          };
        } else {
          const dayIncome = filteredIncome.filter(i => i.date.substring(0, 10) === currentDateStr);
          const dayExpenses = filteredExpenses.filter(e => e.date.substring(0, 10) === currentDateStr);

          return {
            name: dateStr,
            income: dayIncome.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
            expenses: dayExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)
          };
        }
      });

      setChartData(timeSeriesData);

      // Process top categories with null checks
      const incomeByCategory = {};
      filteredIncome.forEach(i => {
        const cat = i.category || 'Uncategorized';
        incomeByCategory[cat] = (incomeByCategory[cat] || 0) + (Number(i.amount) || 0);
      });

      const expenseByCategory = {};
      filteredExpenses.forEach(e => {
        const cat = e.category || 'Uncategorized';
        if (cat !== 'Tax Deposits') {
          expenseByCategory[cat] = (expenseByCategory[cat] || 0) + (Number(e.amount) || 0);
        }
      });

      const topIncome = Object.entries(incomeByCategory)
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 10);

      const topExpense = Object.entries(expenseByCategory)
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 10);

      setTopIncomeCategories(topIncome);
      setTopExpenseCategories(topExpense);
    } catch (err) {
      console.error('Failed to process chart data:', err);
      setError(err.message || 'Failed to load analytics data');
      setChartData([]);
      setTopIncomeCategories([]);
      setTopExpenseCategories([]);
    } finally {
      setLoading(false);
    }
  }, [chartPeriod, currentDate]);

  useEffect(() => {
    processChartData();
  }, [processChartData]);

  const handlePreviousPeriod = () => {
    if (chartPeriod === 'week') {
      setCurrentDate(prev => addWeeks(prev, -1));
    } else if (chartPeriod === 'month') {
      setCurrentDate(prev => addMonths(prev, -1));
    } else {
      setCurrentDate(prev => addYears(prev, -1));
    }
  };

  const handleNextPeriod = () => {
    const now = new Date();
    if (chartPeriod === 'week') {
      const nextWeek = addWeeks(currentDate, 1);
      if (startOfWeek(nextWeek) <= startOfWeek(now)) {
        setCurrentDate(nextWeek);
      }
    } else if (chartPeriod === 'month') {
      const nextMonth = addMonths(currentDate, 1);
      if (startOfMonth(nextMonth) <= startOfMonth(now)) {
        setCurrentDate(nextMonth);
      }
    } else {
      const nextYear = addYears(currentDate, 1);
      if (nextYear <= now) {
        setCurrentDate(nextYear);
      }
    }
  };

  const resetToNow = () => {
    setCurrentDate(new Date());
  };

  const isCurrentPeriod = () => {
    const now = new Date();
    if (chartPeriod === 'week') {
      return startOfWeek(currentDate).getTime() === startOfWeek(now).getTime();
    } else if (chartPeriod === 'month') {
      return startOfMonth(currentDate).getTime() === startOfMonth(now).getTime();
    } else {
      return currentDate.getFullYear() === now.getFullYear();
    }
  };

  const getDateRangeLabel = () => {
    if (chartPeriod === 'week') {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } else if (chartPeriod === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else {
      return format(currentDate, 'yyyy');
    }
  };

  // Calculate quick stats
  const totalIncome = topIncomeCategories.reduce((sum, cat) => sum + cat.amount, 0);
  const totalExpenses = topExpenseCategories.reduce((sum, cat) => sum + cat.amount, 0);
  const netProfit = totalIncome - totalExpenses;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <TrendingUp className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
            <p className="text-sm text-slate-500">Financial insights and trends</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <p className="text-slate-500">Loading analytics...</p>
        </div>
      ) : error ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-12">
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
              <div className="text-center">
                <p className="text-red-700 font-medium">Failed to load analytics</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
              <Button variant="outline" onClick={processChartData} className="mt-2">
                <RotateCcw className="w-4 h-4 mr-2" /> Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-600">Total Income</p>
                    <p className="text-2xl font-bold text-emerald-900">${totalIncome.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-emerald-200 rounded-full">
                    <TrendingUp className="w-6 h-6 text-emerald-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-600">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-900">${totalExpenses.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-red-200 rounded-full">
                    <TrendingUp className="w-6 h-6 text-red-700 rotate-180" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br ${netProfit >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Profit</p>
                    <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>${netProfit.toFixed(2)}</p>
                  </div>
                  <div className={`p-3 rounded-full ${netProfit >= 0 ? 'bg-blue-200' : 'bg-orange-200'}`}>
                    <TrendingUp className={`w-6 h-6 ${netProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Income & Expense Trends */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-lg font-semibold">Income vs Expenses</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">{getDateRangeLabel()}</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Navigation Controls */}
                  <div className="flex items-center gap-1 mr-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreviousPeriod}
                      className="h-8 w-8 p-0"
                      title="Previous period"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetToNow}
                      className="h-8 w-8 p-0"
                      title="Back to current"
                      disabled={isCurrentPeriod()}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPeriod}
                      className="h-8 w-8 p-0"
                      title="Next period"
                      disabled={isCurrentPeriod()}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Period Selector */}
                  <div className="flex gap-1">
                    <Button
                      variant={chartPeriod === 'week' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setChartPeriod('week'); setCurrentDate(new Date()); }}
                      className="h-8 px-3"
                    >
                      Week
                    </Button>
                    <Button
                      variant={chartPeriod === 'month' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setChartPeriod('month'); setCurrentDate(new Date()); }}
                      className="h-8 px-3"
                    >
                      Month
                    </Button>
                    <Button
                      variant={chartPeriod === 'year' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setChartPeriod('year'); setCurrentDate(new Date()); }}
                      className="h-8 px-3"
                    >
                      Year
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {chartData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" style={{ fontSize: '12px' }} />
                      <YAxis style={{ fontSize: '12px' }} />
                      <Tooltip 
                        formatter={(value) => `$${value.toFixed(2)}`}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '14px' }} />
                      <Bar dataKey="income" fill="#10b981" name="Income" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-400 text-sm">No data available for this period</p>
              )}
            </CardContent>
          </Card>

          {/* Top Categories Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Income Sources */}
            {topIncomeCategories.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                    Top Income Sources
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topIncomeCategories.slice(0, 5)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" style={{ fontSize: '12px' }} />
                        <YAxis dataKey="category" type="category" width={120} style={{ fontSize: '12px' }} />
                        <Tooltip 
                          formatter={(value) => `$${value.toFixed(2)}`}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Bar dataKey="amount" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Expense Categories */}
            {topExpenseCategories.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                    Top Expense Categories
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topExpenseCategories.slice(0, 5)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" style={{ fontSize: '12px' }} />
                        <YAxis dataKey="category" type="category" width={120} style={{ fontSize: '12px' }} />
                        <Tooltip 
                          formatter={(value) => `$${value.toFixed(2)}`}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Bar dataKey="amount" fill="#ef4444" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {topIncomeCategories.length === 0 && topExpenseCategories.length === 0 && chartData.length === 0 && (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-20">
                <p className="text-center text-slate-500">
                  No data available yet. Add some income and expenses to see your analytics!
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
